import { GoogleGenerativeAI } from '@google/generative-ai';
import { Investment } from '../types';
// Busca o melhor banco de investimento via IA
export const getBestInvestmentBank = async (
  investmentType: string,
  riskProfile: 'conservative' | 'moderate' | 'aggressive',
  amount: number
): Promise<string> => {
  try {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      // fallback
      return 'XP Investimentos';
    }
    const genAI = new GoogleGenerativeAI(apiKey);
    const modelName = process.env.REACT_APP_GEMINI_MODEL || 'gemini-1.0';
    const model = genAI.getGenerativeModel({ model: modelName });
    const prompt = `Você é um consultor financeiro brasileiro. Para o investimento do tipo "${investmentType}" com perfil de risco "${riskProfile}" e valor de R$${amount.toFixed(2)}, qual o melhor banco de investimento do momento no Brasil? Responda APENAS com o nome do banco, sem explicações.`;
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text().trim();
    return text.split('\n')[0];
  } catch (error) {
    return 'XP Investimentos';
  }
};
// Busca análise de risco detalhada via IA
export const getInvestmentRiskAnalysis = async (
  investmentType: string,
  riskProfile: 'conservative' | 'moderate' | 'aggressive',
  amount: number
): Promise<string> => {
  try {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      return 'Risco padrão para o tipo.';
    }
    const genAI = new GoogleGenerativeAI(apiKey);
    const modelName = process.env.REACT_APP_GEMINI_MODEL || 'gemini-1.0';
    const model = genAI.getGenerativeModel({ model: modelName });
    const prompt = `Você é um consultor financeiro brasileiro. Analise o risco de aportar R$${amount.toFixed(2)} em "${investmentType}" para perfil "${riskProfile}". Responda com uma frase objetiva sobre o risco, sem explicações extras.`;
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text().trim();
    return text.split('\n')[0];
  } catch (error) {
    return 'Risco padrão para o tipo.';
  }
};


// Sugestões de investimento padrão
const getDefaultInvestments = (monthlyProfit: number): Investment[] => {
  const suggestedAmount = monthlyProfit * 0.2;
  return [
    {
      type: 'Tesouro Selic',
      description: 'Investimento de baixo risco com liquidez diária, ideal para reserva de emergência.',
      expectedReturn: 13.75,
      risk: 'low',
      suggestedAmount: suggestedAmount * 0.4
    },
    {
      type: 'Fundos Imobiliários',
      description: 'Diversificação com recebimento de aluguéis mensais e potencial de valorização.',
      expectedReturn: 10.5,
      risk: 'medium',
      suggestedAmount: suggestedAmount * 0.3
    },
    {
      type: 'Ações',
      description: 'Investimento em empresas sólidas com potencial de crescimento a longo prazo.',
      expectedReturn: 15.0,
      risk: 'high',
      suggestedAmount: suggestedAmount * 0.3
    }
  ];
};


export interface FinancialDecisionSuggestion {
  title: string;
  description: string;
  actionItems?: string[];
  impact?: string;
}


export const getInvestmentSuggestions = async (
  monthlyProfit: number,
  riskProfile: 'conservative' | 'moderate' | 'aggressive'
): Promise<Investment[]> => {
  try {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      return getDefaultInvestments(monthlyProfit);
    }

    const genAI = new GoogleGenerativeAI(apiKey);
    const modelName = process.env.REACT_APP_GEMINI_MODEL || 'gemini-1.0';
    const model = genAI.getGenerativeModel({ model: modelName });

    const prompt = `Você é um consultor financeiro especializado em investimentos para trabalhadores autônomos no Brasil.

Um trabalhador autônomo tem lucro mensal de R$ ${monthlyProfit.toFixed(2)} e perfil de risco: ${riskProfile === 'conservative' ? 'conservador' : riskProfile === 'moderate' ? 'moderado' : 'agressivo'}.

Sugira 3 opções de investimento adequadas ao perfil. Responda APENAS com um JSON válido no formato:
[
  {
    "type": "Nome do investimento",
    "description": "Descrição clara e objetiva",
    "expectedReturn": número (retorno anual em %),
    "risk": "low" ou "medium" ou "high",
    "suggestedAmount": número (valor sugerido em R$)
  }
]

Não inclua markdown, explicações extras ou texto fora do JSON.`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    const cleanText = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const suggestions = JSON.parse(cleanText);

    if (Array.isArray(suggestions) && suggestions.length > 0) {
      return suggestions.map((s: any) => ({
        type: s.type || 'Investimento',
        description: s.description || 'Sem descrição',
        expectedReturn: Number(s.expectedReturn) || 0,
        risk: s.risk || 'medium',
        suggestedAmount: Number(s.suggestedAmount) || monthlyProfit * 0.2
      }));
    }

    return getDefaultInvestments(monthlyProfit);
  } catch (error) {
    console.error('Error getting Gemini suggestions:', error);
    return getDefaultInvestments(monthlyProfit);
  }
};


// Sugestões de decisões financeiras padrão
const getDefaultFinancialDecisions = (
  totalRevenue: number,
  totalProfit: number,
  impostoTotal: number,
  saldo: number
): FinancialDecisionSuggestion[] => [
  {
    title: 'Construir Reserva de Emergência',
    description: 'Separe pelo menos 20% do lucro (' + String((totalProfit * 0.2).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })) + ') para sua reserva de emergência.',
    actionItems: [
      'Abra uma conta poupança dedicada para emergências',
      'Configure transferência automática mensal',
      'Mantenha pelo menos 3-6 meses de despesas'
    ],
    impact: 'Impacto positivo: Segurança financeira e tranquilidade'
  },
  {
    title: 'Antecipar Reserva de Impostos',
    description: 'Considere reservar o valor estimado de impostos (' + String(impostoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })) + ') para evitar surpresas.',
    actionItems: [
      'Separe ' + String((impostoTotal / 4).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })) + ' por mês em conta separada',
      'Agende reunião com contador',
      'Revise classificação fiscal anualmente'
    ],
    impact: 'Impacto positivo: Planejamento fiscal adequado'
  },
  {
    title: 'Avaliar Reinvestimento em Crescimento',
    description: 'Com saldo de ' + String(saldo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })) + ', avalie reinvestir parte dos ganhos em aplicações de baixo risco.',
    actionItems: [
      'Analise opções de Tesouro Direto',
      'Compare taxas de fundos de renda fixa',
      'Comece com investimento mínimo'
    ],
    impact: 'Impacto positivo: Crescimento patrimonial'
  }
];



export const getFinancialDecisionSuggestions = async (
  totalRevenue: number,
  totalProfit: number,
  impostoTotal: number,
  riskProfile: 'conservative' | 'moderate' | 'aggressive',
  saldo: number
): Promise<FinancialDecisionSuggestion[]> => {
  try {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      return getDefaultFinancialDecisions(totalRevenue, totalProfit, impostoTotal, saldo);
    }

    const genAI = new GoogleGenerativeAI(apiKey);
    const modelName = process.env.REACT_APP_GEMINI_MODEL || 'gemini-1.0';
    const model = genAI.getGenerativeModel({ model: modelName });

    const prompt = 'Você é um consultor financeiro para autônomos no Brasil. Analise os dados abaixo e sugira 3 decisões financeiras práticas e personalizadas para o próximo mês. Responda APENAS com um JSON válido no formato solicitado.\n\n' +
      'Dados:\n' +
      '- Receita total: R$ ' + totalRevenue.toFixed(2) + '\n' +
      '- Lucro total: R$ ' + totalProfit.toFixed(2) + '\n' +
      '- Impostos estimados: R$ ' + impostoTotal.toFixed(2) + '\n' +
      '- Perfil de risco: ' + riskProfile + '\n' +
      '- Saldo em conta: R$ ' + saldo.toFixed(2) + '\n\n' +
      'Formato de resposta:\n[\n  {\n    "title": "Título da decisão",\n    "description": "Descrição clara e objetiva",\n    "actionItems": ["ação 1", "ação 2", "ação 3"],\n    "impact": "Impacto positivo ou negativo estimado"\n  }\n]\n\nNão inclua markdown, explicações extras ou texto fora do JSON.';

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    const cleanText = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const suggestions = JSON.parse(cleanText);

    if (Array.isArray(suggestions) && suggestions.length > 0) {
      return suggestions.map((s: any) => ({
        title: s.title || 'Decisão',
        description: s.description || 'Sem descrição',
        actionItems: Array.isArray(s.actionItems) ? s.actionItems : undefined,
        impact: s.impact || undefined
      }));
    }

    return getDefaultFinancialDecisions(totalRevenue, totalProfit, impostoTotal, saldo);
  } catch (error) {
    console.error('Error getting Gemini financial decisions:', error);
    return getDefaultFinancialDecisions(totalRevenue, totalProfit, impostoTotal, saldo);
  }
};

export const getAiAnalysis = async (
  totalRevenue: number,
  totalProfit: number,
  impostoTotal: number,
  riskProfile: 'conservative' | 'moderate' | 'aggressive',
  saldo: number
) => {
  const [investmentSuggestions, financialDecisionSuggestions] = await Promise.all([
    getInvestmentSuggestions(totalProfit, riskProfile),
    getFinancialDecisionSuggestions(totalRevenue, totalProfit, impostoTotal, riskProfile, saldo),
  ]);

  return { investmentSuggestions, financialDecisionSuggestions };
};
